/**
 * Export Lookup Request (piece 16, *Manufacturer lookup*; decision 12660; T3814):
 * the file naming the ticked Items for a person or an outside agent to fill as a
 * lookup bundle. It names each Item by our id, its manufacturer and part number,
 * the attributes it holds with their types and units, and the fields still
 * empty, so whoever fills it knows what to find. 🔨 DOM-free.
 */
import { LOOKUP_FIELDS, LOOKUP_FILES_FOLDER, LOOKUP_MANIFEST_NAME, type LookupField } from './manufacturerLookup';

/** The route the Items list's Export Lookup Request posts the ticked Items to. */
export const ITEM_LOOKUP_REQUEST_PATH = '/catalog/items/lookup-request';

/** One attribute an Item holds, with its type's kind and the unit its value is in. */
export interface LookupRequestAttribute {
  name: string;
  kind: 'Measured' | 'Enumerated';
  value: string;
  unit: string | null;
  /** The units a Measured type's family takes, by abbreviation; the choices an Enumerated type offers. */
  takes: string[];
}

/** One Item the request names. */
export interface LookupRequestItem {
  id: string;
  sku: string;
  name: string;
  manufacturer: string | null;
  partNumber: string | null;
  referenceName: string | null;
  category: string;
  source: string | null;
  attributes: LookupRequestAttribute[];
  /** The category's attribute types the Item holds no value for, with what each takes. */
  attributesMissing: Omit<LookupRequestAttribute, 'value' | 'unit'>[];
  /** The lookup fields the Item holds nothing for, in the manifest's own names, and `itemImage` where it has no picture. */
  fieldsEmpty: (LookupField | 'itemImage')[];
}

/** The whole file. */
export interface LookupRequestFile {
  /** What the file is and what to send back, in one paragraph an agent reads first. */
  instructions: string;
  requestedOn: string;
  /** The File Type names a document's `kind` may name. */
  documentKinds: string[];
  /** The lifecycle states a `lifecycle` field may read. */
  lifecycleStates: string[];
  items: LookupRequestItem[];
}

/** 🔒 The paragraph the file opens on: the bundle's contract in the words of piece 16. */
export function lookupRequestInstructions(): string {
  return [
    `Fill a lookup bundle for the items below and send it back: a zip holding ${LOOKUP_MANIFEST_NAME} and a ${LOOKUP_FILES_FOLDER} folder.`,
    'The manifest is a JSON object whose items list holds one entry per item, named by the id given here, with the source address the facts were read from.',
    `An entry's fields may name ${LOOKUP_FIELDS.join(', ')}; listPrice is a number, lifecycle one of the lifecycle states, the rest text.`,
    'Its attributes list holds entries of name, value and unit; its documents list entries of file, title and kind, the kind one of the document kinds; its pictures list entries of file and itemImage, true on at most one picture.',
    `Every file names a file in the ${LOOKUP_FILES_FOLDER} folder by its name alone. Nothing is written to an item until a person steps through it and accepts each line.`,
  ].join(' ');
}

/** The file's name, dated the day it was made in the Organization's zone. */
export function lookupRequestFilename(day: string): string {
  return `lookup-request-${day}.json`;
}
